import moment from 'moment';
import type { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import CONSTANTS, { IS_MEC_RUN_ALWAYS_ENABLED } from 'src/utils/constants';

/**
 * Accounting periods for a producer's Period select, newest first. Anchored to the newest *closed* period so
 * Execute never defaults to books that haven't closed: prod/beta close the previous period during the current
 * calendar month, so the window ends last month there, whereas dev/alpha/preprod (IS_MEC_RUN_ALWAYS_ENABLED) may
 * run the current month. Mirrors the same guard in the calculation/worksheet status bars.
 *
 * The option LABEL is the MDA "MMM-YYYY" display convention (e.g. "APR-2026"); the option VALUE is the
 * canonical `yyyyMM` period id (e.g. "202604") the endpoints expect — display and wire formats are kept
 * decoupled per the package's PERIOD_DISPLAY_FORMAT / PERIOD_VALUE_FORMAT split, so we never send the
 * human-readable label to the backend as the period.
 */
export function generateAccountingPeriods(openPeriod?: string, count = 12): SelectProps.Option[] {
    // Anchor the list at the backend open period when known (newest option = openPeriod), so the selector
    // defaults to the editable period. Fall back to the wall-clock newest month until the open period
    // resolves. Only that period is editable; the earlier options are read-only historical views.
    const anchor = openPeriod
        ? moment(openPeriod, CONSTANTS.PERIOD_VALUE_FORMAT)
        : IS_MEC_RUN_ALWAYS_ENABLED
          ? moment()
          : moment().subtract(1, 'months');
    const start = anchor.date(1);
    return Array.from({ length: count }, (_, i) => {
        const month = start.clone().subtract(i, 'months');
        return {
            label: month.format(CONSTANTS.PERIOD_DISPLAY_FORMAT).toUpperCase(),
            value: month.format(CONSTANTS.PERIOD_VALUE_FORMAT),
        };
    });
}

/**
 * The hand-off read's open period as `yyyyMM`, the shape the period options use, so the open-period comparison
 * holds even if that read's period shape ever drifts — otherwise no option would match and every period would
 * read as "past". A no-op for a `yyyyMM` value; unparseable gives undefined (not the raw string), failing open to
 * the wall-clock anchor rather than a value that matches no option.
 */
export function normaliseOpenPeriod(rawOpenPeriod?: string | null): string | undefined {
    if (!rawOpenPeriod) return undefined;
    if (moment(rawOpenPeriod, CONSTANTS.PERIOD_VALUE_FORMAT, true).isValid()) return rawOpenPeriod;
    const alt = moment(rawOpenPeriod, ['MMM-YY', 'MMM-YYYY', 'MMMM YYYY', 'YYYY-MM'], true);
    return alt.isValid() ? alt.format(CONSTANTS.PERIOD_VALUE_FORMAT) : undefined;
}
